// SCR-STAFF-308 — سیاست‌های نوبت‌دهی (US-SCH-040 تا 042؛ BR-SCH-041 تا 043): سیاست به ازای مستأجر، شعبه، دسته یا خدمت با وراثت «اولین
// مقدار تعریف‌شده» (خدمت ← دسته ← شعبه ← مستأجر ← پیش‌فرض)، مهلت لغو، بیعانه (درصد یا مبلغ ثابت ریالی) و مهلت پرداخت، ضبط، حداقل فاصله،
// افق، سقف Overbooking و آستانه غیبت؛ وضعیت محدودیت رزرو آنلاین مراجعه‌کننده و رفع آن با دلیل (AC-SCH-042.2). خدمت هدف با جستجوی
// سروری انتخاب می‌شود چون booking-options فقط ۱۰۰ خدمت نخست را برمی‌گرداند (C-AGENT-1055).
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BookingServiceOption, type PolicyScope, type PolicyValues, type PolicyView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { BusinessDateField, DataTable, faDigits, FormDialog, latinDigits, ListPage, ReasonDialog, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { lockPolicyValue } from './appointmentLock';
import { PatientPicker, type BookingPatient } from './BookingDialog';
import { schKeys, useActiveBranch, useBookingOptions, useServiceSearch } from './common';

export const SCR_STAFF_308 = 'SCR-STAFF-308';

const SCOPES: readonly PolicyScope[] = ['Tenant', 'Branch', 'Category', 'Service'];

/** پارامترهای عددی سیاست به ترتیب نمایش (PRD-SCH §5). */
const NUMBER_FIELDS = [
  'cancellationWindowHours',
  'depositPercent',
  'depositAmount',
  'depositDueHours',
  'onlineDepositDueHours',
  'lateCancelForfeitPercent',
  'noShowForfeitPercent',
  'minLeadMinutes',
  'horizonDays',
  'onlineHorizonDays',
  'maxActiveOnline',
  'overbookDailyLimit',
  'noShowThreshold',
  'noShowWindowMonths',
] as const satisfies readonly (keyof PolicyValues)[];

type NumberField = (typeof NUMBER_FIELDS)[number];
type DepositDueMode = 'AfterBooking' | 'BeforeStart' | 'UntilStart' | 'AtCenter';
type OnDepositUnpaid = 'CancelAndRelease' | 'KeepAppointment';
const DEPOSIT_DUE_MODES: readonly DepositDueMode[] = ['AfterBooking', 'BeforeStart', 'UntilStart', 'AtCenter'];
const ON_DEPOSIT_UNPAID: readonly OnDepositUnpaid[] = ['CancelAndRelease', 'KeepAppointment'];
/** فاصله‌های مجاز BeforeStart (دقیقه): ۳ ساعت و ۱، ۲، ۳، ۷، ۱۴ روز. */
const BEFORE_START_OFFSETS = [180, 1_440, 2_880, 4_320, 10_080, 20_160] as const;
type Draft = Record<NumberField, string> & {
  scope: PolicyScope;
  scopeId: string;
  /** نام هدف سیاست موجود (برای خدمتی که در نتایج جستجوی جاری نیست). */
  scopeName: string | null;
  depositRequired: '' | 'true' | 'false';
  horizonEndDate: string;
  onlineHorizonEndDate: string;
  depositDueMode: '' | DepositDueMode;
  depositDueOffsetMinutes: string;
  onDepositUnpaid: '' | OnDepositUnpaid;
};

function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function daysUntil(iso: string | null | undefined): number | null {
  const date = iso ? parseIsoDate(iso) : null;
  if (!date) {
    return null;
  }
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((date.getTime() - today.getTime()) / 86_400_000);
}

function draftOf(policy: PolicyView | null): Draft {
  const values = policy?.values ?? {};
  const numbers = Object.fromEntries(
    NUMBER_FIELDS.map((field) => [field, values[field] === null || values[field] === undefined ? '' : String(values[field])]),
  ) as Record<NumberField, string>;
  return {
    ...numbers,
    scope: policy?.scope ?? 'Tenant',
    scopeId: policy?.scopeId ?? '',
    scopeName: policy?.scopeName ?? null,
    depositRequired: values.depositRequired === true ? 'true' : values.depositRequired === false ? 'false' : '',
    horizonEndDate: values.horizonEndDate ?? '',
    onlineHorizonEndDate: values.onlineHorizonEndDate ?? '',
    depositDueMode: (values.depositDueMode as DepositDueMode | null | undefined) ?? '',
    depositDueOffsetMinutes: values.depositDueOffsetMinutes === null || values.depositDueOffsetMinutes === undefined ? '' : String(values.depositDueOffsetMinutes),
    onDepositUnpaid: (values.onDepositUnpaid as OnDepositUnpaid | null | undefined) ?? '',
  };
}

function valuesOf(draft: Draft): PolicyValues {
  const values: PolicyValues = {};
  for (const field of NUMBER_FIELDS) {
    const raw = latinDigits(draft[field]).trim();
    values[field] = raw === '' ? null : Number(raw);
  }
  values.depositRequired = draft.depositRequired === '' ? null : draft.depositRequired === 'true';
  values.horizonEndDate = draft.horizonEndDate || null;
  values.onlineHorizonEndDate = draft.onlineHorizonEndDate || null;
  values.depositDueMode = draft.depositDueMode === '' ? null : draft.depositDueMode;
  values.depositDueOffsetMinutes =
    draft.depositDueMode === 'BeforeStart' && draft.depositDueOffsetMinutes.trim() !== ''
      ? Number(latinDigits(draft.depositDueOffsetMinutes))
      : null;
  values.onDepositUnpaid = draft.onDepositUnpaid === '' ? null : draft.onDepositUnpaid;
  return values;
}

/** خلاصه مقادیر تعریف‌شده سیاست (فقط پارامترهای غیرتهی). */
function Summary({ values }: { values: PolicyValues }) {
  const { t } = useTranslation('sch');
  const parts = [
    ...(values.depositRequired !== null && values.depositRequired !== undefined
      ? [`${t('sch:policies.field.depositRequired')}: ${values.depositRequired ? t('sch:policies.yes') : t('sch:policies.no')}`]
      : []),
    ...(values.depositDueMode
      ? [`${t('sch:policies.field.depositDueMode')}: ${t(`sch:policies.deposit_due_mode.${values.depositDueMode}`)}`]
      : []),
    ...(values.onDepositUnpaid
      ? [`${t('sch:policies.field.onDepositUnpaid')}: ${t(`sch:policies.on_deposit_unpaid.${values.onDepositUnpaid}`)}`]
      : []),
    ...NUMBER_FIELDS.filter((field) => values[field] !== null && values[field] !== undefined).map(
      (field) => `${t(`sch:policies.field.${field}`)}: ${faDigits(String(values[field]))}`,
    ),
  ];
  return <span>{parts.length > 0 ? parts.join(' · ') : t('sch:policies.inherits')}</span>;
}

/** وضعیت محدودیت رزرو آنلاین مراجعه‌کننده و رفع با دلیل (BR-SCH-042). */
function RestrictionCard() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [patient, setPatient] = useState<BookingPatient | null>(null);
  const [lifting, setLifting] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const key = ['scheduling', session?.tenantId, 'booking-status', patient?.id ?? ''] as const;
  const status = useQuery({
    queryKey: key,
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/scheduling/patients/{patientId}/booking-status', { params: { path: { patientId: patient?.id ?? '' } } })),
    enabled: patient !== null,
  });
  const lift = useMutation({
    mutationFn: (reason: string) =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/patients/{patientId}/booking-restriction:lift', {
          params: { path: { patientId: patient?.id ?? '' } },
          body: { reason },
        }),
      ),
    onSuccess: () => {
      setLifting(false);
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="subtitle1" component="h2">
            {t('sch:policies.restriction_title')}
          </Typography>
          <PatientPicker value={patient} onChange={setPatient} disabled={false} inputRef={input} />
          {status.data ? (
            <Stack direction="row" spacing={2} alignItems="center" data-testid="restriction-status">
              <Alert severity={status.data.restricted ? 'warning' : 'success'} sx={{ flex: 1 }}>
                {status.data.restricted
                  ? t('sch:policies.restricted', { count: faDigits(Number(status.data.noShowCount)), threshold: faDigits(Number(status.data.threshold)) })
                  : t('sch:policies.not_restricted', { count: faDigits(Number(status.data.noShowCount)) })}
              </Alert>
              {status.data.restricted ? (
                <Button variant="outlined" onClick={() => setLifting(true)} data-testid="restriction-lift">
                  {t('sch:policies.lift')}
                </Button>
              ) : null}
            </Stack>
          ) : null}
        </Stack>
      </CardContent>
      <ReasonDialog
        open={lifting}
        titleKey="sch:policies.lift"
        destructive={false}
        busy={lift.isPending}
        error={lift.error}
        onConfirm={(reason) => lift.mutate(reason)}
        onCancel={() => setLifting(false)}
      />
    </Card>
  );
}

export function BookingPoliciesPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session, activeTenant } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { branchId } = useActiveBranch();
  const options = useBookingOptions(branchId);
  const [editing, setEditing] = useState<Draft | null>(null);
  const serviceSearch = useServiceSearch(branchId, editing?.scope === 'Service');
  const tenantId = session?.tenantId;
  const policies = useQuery({
    queryKey: schKeys.policies(tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/policies')),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: schKeys.all(tenantId) });
  const save = useMutation({
    mutationFn: (draft: Draft) =>
      unwrap(
        tenantApi.PUT('/api/v1/scheduling/policies', {
          body: { scope: draft.scope, scopeId: draft.scope === 'Tenant' ? null : draft.scopeId || null, values: valuesOf(draft) },
        }),
      ),
    onSuccess: () => {
      setEditing(null);
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(tenantApi.DELETE('/api/v1/scheduling/policies/{id}', { params: { path: { id } } })),
    onSuccess: refresh,
  });
  const full = access.accessMode === 'Full';
  const targets = (scope: PolicyScope): { id: string; name: string }[] =>
    scope === 'Branch'
      ? (activeTenant?.branches ?? []).map((branch) => ({ id: branch.branchId, name: branch.name ?? '' }))
      : scope === 'Category'
        ? (options.data?.categories ?? []).map((category) => ({ id: category.id, name: category.name ?? '' }))
        : [];
  /** خدمت انتخاب‌شده: از نتایج دیده‌شده جستجو یا نام ذخیره‌شده سیاست موجود. */
  const selectedService = (draft: Draft): BookingServiceOption | null => {
    if (draft.scopeId === '') {
      return null;
    }
    const seen = serviceSearch.find(draft.scopeId);
    return seen ?? ({ id: draft.scopeId, code: '', name: draft.scopeName ?? draft.scopeId, perUnit: false, durationMinutes: 0 } as BookingServiceOption);
  };
  const columns: DataTableColumn<PolicyView>[] = [
    { id: 'scope', headerKey: 'sch:policies.columns.scope', cell: (row) => t(`sch:policies.scope.${row.scope}`) },
    {
      id: 'target',
      headerKey: 'sch:policies.columns.target',
      cell: (row) => row.scopeName ?? targets(row.scope).find((item) => item.id === row.scopeId)?.name ?? '—',
    },
    { id: 'values', headerKey: 'sch:policies.columns.values', cell: (row) => <Summary values={row.values} /> },
    {
      id: 'actions',
      headerKey: 'sch:policies.columns.actions',
      cell: (row) =>
        full ? (
          <Stack direction="row" spacing={1}>
            <Button size="small" onClick={() => setEditing(draftOf(row))} data-testid="policy-edit">
              {t('common:actions.edit')}
            </Button>
            <Button size="small" color="error" onClick={() => remove.mutate(row.id)} data-testid="policy-delete">
              {t('common:actions.delete')}
            </Button>
          </Stack>
        ) : null,
    },
  ];
  const defaults = policies.data?.defaults;
  return (
    <ListPage
      screenId={SCR_STAFF_308}
      titleKey="sch:policies.title"
      subtitleKey="sch:policies.subtitle"
      actions={
        full ? (
          <Button variant="contained" onClick={() => setEditing(draftOf(null))} data-testid="policy-new">
            {t('sch:policies.new')}
          </Button>
        ) : undefined
      }
    >
      <DataTable
        captionKey="sch:policies.title"
        columns={columns}
        rows={policies.data?.items}
        getRowId={(row) => row.id}
        isLoading={policies.isPending}
        error={policies.error ?? remove.error}
        onRetry={() => void policies.refetch()}
        emptyTitleKey="sch:policies.empty"
      />
      {(policies.data?.items ?? []).some((row) => {
        const staff = daysUntil(row.values.horizonEndDate);
        const online = daysUntil(row.values.onlineHorizonEndDate);
        return (staff !== null && staff >= 0 && staff <= 14) || (online !== null && online >= 0 && online <= 14);
      }) ? (
        <Alert severity="warning" data-testid="horizon-ending">
          {t('sch:policies.horizon_ending')}
        </Alert>
      ) : null}
      {defaults ? (
        <Typography variant="body2" color="text.secondary" data-testid="policy-defaults">
          {t('sch:policies.defaults')}: <Summary values={defaults} />
        </Typography>
      ) : null}
      <LockSection />
      <RestrictionCard />
      <FormDialog
        open={editing !== null}
        titleKey="sch:policies.edit_title"
        screenId={SCR_STAFF_308}
        onSubmit={() => editing && save.mutate(editing)}
        onClose={() => setEditing(null)}
        isDirty
        isSubmitting={save.isPending}
        submitError={save.error}
        maxWidth="md"
      >
        {editing ? (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label={t('sch:policies.columns.scope')}
                value={editing.scope}
                onChange={(event) => setEditing({ ...editing, scope: event.target.value as PolicyScope, scopeId: '', scopeName: null })}
                sx={{ minWidth: 160 }}
                inputProps={{ 'data-testid': 'policy-scope' }}
              >
                {SCOPES.map((scope) => (
                  <MenuItem key={scope} value={scope}>
                    {t(`sch:policies.scope.${scope}`)}
                  </MenuItem>
                ))}
              </TextField>
              {editing.scope === 'Service' ? (
                <Autocomplete<BookingServiceOption, false, false, false>
                  value={selectedService(editing)}
                  onChange={(_event, next) => setEditing({ ...editing, scopeId: next?.id ?? '', scopeName: next?.name ?? null })}
                  options={serviceSearch.services}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(items) => items}
                  onInputChange={(_event, value, reason) => {
                    if (reason === 'input') {
                      serviceSearch.search(value);
                    }
                  }}
                  loading={serviceSearch.isFetching}
                  noOptionsText={t('sch:booking.service_none')}
                  sx={{ flex: 1, minWidth: 220 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      required
                      label={t('sch:policies.columns.target')}
                      inputProps={{ ...params.inputProps, 'data-testid': 'policy-target' }}
                    />
                  )}
                />
              ) : editing.scope !== 'Tenant' ? (
                <TextField
                  select
                  required
                  label={t('sch:policies.columns.target')}
                  value={editing.scopeId}
                  onChange={(event) => setEditing({ ...editing, scopeId: event.target.value })}
                  sx={{ flex: 1 }}
                  inputProps={{ 'data-testid': 'policy-target' }}
                >
                  {targets(editing.scope).map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              <TextField
                select
                label={t('sch:policies.field.depositRequired')}
                value={editing.depositRequired}
                onChange={(event) => setEditing({ ...editing, depositRequired: event.target.value as Draft['depositRequired'] })}
                sx={{ minWidth: 160 }}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'policy-deposit-required' }}
              >
                <MenuItem value="">{t('sch:policies.inherit')}</MenuItem>
                <MenuItem value="true">{t('sch:policies.yes')}</MenuItem>
                <MenuItem value="false">{t('sch:policies.no')}</MenuItem>
              </TextField>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {t('sch:policies.inherit_hint')}
            </Typography>
            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
              <TextField
                select
                label={t('sch:policies.field.depositDueMode')}
                value={editing.depositDueMode}
                onChange={(event) =>
                  setEditing({
                    ...editing,
                    depositDueMode: event.target.value as Draft['depositDueMode'],
                    depositDueOffsetMinutes: event.target.value === 'BeforeStart' ? editing.depositDueOffsetMinutes || '1440' : '',
                  })
                }
                sx={{ width: 220 }}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'policy-depositDueMode' }}
              >
                <MenuItem value="">{t('sch:policies.inherit')}</MenuItem>
                {DEPOSIT_DUE_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {t(`sch:policies.deposit_due_mode.${mode}`)}
                  </MenuItem>
                ))}
              </TextField>
              {editing.depositDueMode === 'BeforeStart' ? (
                <TextField
                  select
                  label={t('sch:policies.field.depositDueOffsetMinutes')}
                  value={editing.depositDueOffsetMinutes}
                  onChange={(event) => setEditing({ ...editing, depositDueOffsetMinutes: event.target.value })}
                  sx={{ width: 220 }}
                  inputProps={{ 'data-testid': 'policy-depositDueOffsetMinutes' }}
                >
                  {BEFORE_START_OFFSETS.map((minutes) => (
                    <MenuItem key={minutes} value={String(minutes)}>
                      {t(`sch:policies.before_start_offset.${minutes}`)}
                    </MenuItem>
                  ))}
                </TextField>
              ) : null}
              <TextField
                select
                label={t('sch:policies.field.onDepositUnpaid')}
                value={editing.onDepositUnpaid}
                onChange={(event) => setEditing({ ...editing, onDepositUnpaid: event.target.value as Draft['onDepositUnpaid'] })}
                sx={{ width: 220 }}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ 'data-testid': 'policy-onDepositUnpaid' }}
              >
                <MenuItem value="">{t('sch:policies.inherit')}</MenuItem>
                {ON_DEPOSIT_UNPAID.map((reaction) => (
                  <MenuItem key={reaction} value={reaction}>
                    {t(`sch:policies.on_deposit_unpaid.${reaction}`)}
                  </MenuItem>
                ))}
              </TextField>
              {(['horizonEndDate', 'onlineHorizonEndDate'] as const).map((field) => (
                <BusinessDateField
                  key={field}
                  label={t(`sch:policies.field.${field}`)}
                  value={editing[field]}
                  onChange={(next) => setEditing({ ...editing, [field]: next })}
                  testId={`policy-${field}`}
                />
              ))}
              {NUMBER_FIELDS.map((field) => (
                <TextField
                  key={field}
                  label={t(`sch:policies.field.${field}`)}
                  value={editing[field]}
                  onChange={(event) => setEditing({ ...editing, [field]: event.target.value })}
                  placeholder={defaults?.[field] !== null && defaults?.[field] !== undefined ? faDigits(String(defaults[field])) : ''}
                  inputProps={{ inputMode: 'numeric', dir: 'ltr', 'data-testid': `policy-${field}` }}
                  sx={{ width: 220 }}
                />
              ))}
            </Stack>
          </>
        ) : null}
      </FormDialog>
    </ListPage>
  );
}

function LockSection() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const enabled = access.can('cfg.settings.view') && access.can('sch.policy.manage');
  const lock = useQuery({
    queryKey: ['sch', session?.tenantId, 'appointment-lock'],
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/configuration/settings/{key}', { params: { path: { key: 'sch.appointment_lock_status' } } })),
    enabled,
  });
  const capacity = useQuery({
    queryKey: ['sch', session?.tenantId, 'remaining-capacity'],
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/configuration/settings/{key}', { params: { path: { key: 'sch.public_show_remaining_capacity' } } })),
    enabled,
  });
  const save = useMutation({
    mutationFn: (input: { key: string; value: string | boolean }) =>
      unwrap(
        tenantApi.PUT('/api/v1/configuration/settings/{key}', {
          params: { path: { key: input.key } },
          body: { scope: 'Tenant', scopeId: null, value: input.value },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['sch', session?.tenantId, 'appointment-lock'] });
      await queryClient.invalidateQueries({ queryKey: ['sch', session?.tenantId, 'remaining-capacity'] });
    },
  });
  if (!enabled) {
    return null;
  }
  const policy = lockPolicyValue(lock.data?.value);
  const shown = capacity.data?.value === true;
  return (
    <Card variant="outlined" data-testid="appointment-lock-settings">
      <CardContent>
        <Stack spacing={2}>
          <Typography variant="h6" component="h2">
            {t('sch:policies.lock_title')}
          </Typography>
          <TextField
            select
            label={t('sch:policies.lock_status')}
            value={policy}
            onChange={(event) => save.mutate({ key: 'sch.appointment_lock_status', value: event.target.value })}
            inputProps={{ 'data-testid': 'appointment-lock-status' }}
          >
            <MenuItem value="Off">{t('sch:policies.lock_off')}</MenuItem>
            <MenuItem value="CheckedIn">{t('sch:policies.lock_checked_in')}</MenuItem>
            <MenuItem value="InProgress">{t('sch:policies.lock_in_progress')}</MenuItem>
          </TextField>
          <FormControlLabel
            data-testid="public-remaining-capacity"
            control={
              <Checkbox
                checked={shown}
                onChange={(event) => save.mutate({ key: 'sch.public_show_remaining_capacity', value: event.target.checked })}
              />
            }
            label={t('sch:policies.show_capacity')}
          />
        </Stack>
      </CardContent>
    </Card>
  );
}
